import { z } from 'zod';
import { TileType } from '@/models/TileTypes';
import {
  ChipIdSchema,
  ChipInstanceIdSchema,
  CircuitTypeSchema,
  GlobalTilePositionSchema,
  InterfaceLinkInstanceIdSchema,
  PointDataSchema,
} from '@/schemas/common';
import { CircuitDtoSchema } from '@/schemas/circuit';
import { LogicGraphDtoSchema } from '@/schemas/logic';

export const ChipInstanceDtoSchema = z.object({
  bundleWidth: z.number().int().positive().max(4096).optional(),
  id: ChipInstanceIdSchema,
  chipId: ChipIdSchema,
  originPointDto: PointDataSchema,
  logicGraphDto: LogicGraphDtoSchema,
  originGlobalTileLocation: GlobalTilePositionSchema.optional(),
  templateToInstanceIdMap: z.record(z.string(), z.string()).optional(),
  rotation: z.number().int().min(0).max(3).optional(),
});

// --- InterfaceLink DTO ---

export const InterfaceTypeDtoSchema = z.number().int().min(0).max(3);

export const MISideConfigDtoSchema = z.object({
  active: z.boolean(),
  forceConnection: z.boolean(),
});

export const InterfaceLinkDtoSchema = z.object({
  id: InterfaceLinkInstanceIdSchema,
  type: InterfaceTypeDtoSchema,
  label: z.string(),
  selectorSignalIds: z.array(z.string().min(1).max(256)).optional(),
  color: z.string(),
  logicTileId: z.string().optional(),
  structureTileId: z.string().optional(),
  sideLabels: z.record(z.string(), z.string()).optional(),
  inputMode: z.string().optional(),
  labelHidden: z.boolean().optional(),
  displayMode: z.string().optional(),
  gradientColor: z.string().nullable().optional(),
  stateColors: z.record(z.string(), z.string()).optional(),
  stateImages: z.record(z.string(), z.string()).optional(),
  statePixelArt: z.record(z.string(), z.string()).optional(),
  sideConfig: z.record(z.string(), MISideConfigDtoSchema).optional(),
});

export type InterfaceLinkDto = z.infer<typeof InterfaceLinkDtoSchema>;

// --- ChipModel DTO ---

export const ChipModelDtoSchema = z
  .object({
    kind: z.literal('bundle-adapter').optional(),
    /** Persistent arrangements share storage/editor infrastructure, never placement identity. */
    templateType: z.enum(['logic', 'structure']).optional(),
    /** Authored tile coordinate used as the stamping cursor and rotation pivot. */
    templateOrigin: PointDataSchema.extend({
      x: z.number().int(),
      y: z.number().int(),
    }).optional(),
    adapterSignals: z.record(z.string(), z.string().min(1).max(256)).optional(),
    id: ChipIdSchema,
    folder: z.string().trim().max(80).optional(),
    name: z.string(),
    description: z.string(),
    color: z.string(),
    combinedColorDisplay: z.boolean().optional(),
    logicCircuitDto: CircuitDtoSchema,
    structureCircuitDto: CircuitDtoSchema,
    structureOriginLocationDto: PointDataSchema.optional(),
    interfaceLinks: z.array(InterfaceLinkDtoSchema),
    chipInstanceDtos: z.array(ChipInstanceDtoSchema),
    editingInstanceDto: ChipInstanceDtoSchema,
    // A bounded, versioned optional hint. Runtime admission validates known
    // versions; unknown versions retain the ordinary native load fallback.
    nativeContinuation: z.unknown().optional(),
  })
  .superRefine((chip, ctx) => {
    if (chip.templateType) {
      if (chip.kind)
        ctx.addIssue({
          code: 'custom',
          path: ['kind'],
          message: 'Templates cannot be bundle adapters',
        });
      const opposite =
        chip.templateType === 'logic' ? chip.structureCircuitDto : chip.logicCircuitDto;
      if (
        opposite.tileManagerDto.tiles.length ||
        (chip.templateType === 'structure' && chip.chipInstanceDtos.length)
      )
        ctx.addIssue({
          code: 'custom',
          path: ['templateType'],
          message: 'Templates contain only their chosen grid type',
        });
      const oppositeTileKey =
        chip.templateType === 'logic' ? 'structureTileId' : 'logicTileId';
      if (chip.interfaceLinks.some((link) => link[oppositeTileKey]))
        ctx.addIssue({
          code: 'custom',
          path: ['interfaceLinks'],
          message: 'Template interfaces belong only to their chosen grid',
        });
    }
    if (chip.kind !== 'bundle-adapter') return;
    if (
      chip.logicCircuitDto.tileManagerDto.tiles.length ||
      chip.chipInstanceDtos.length
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['logicCircuitDto'],
        message:
          'Bundle adapters are Structure-only and cannot contain Logic tiles or embedded chips',
      });
    }
    const identityLabels = new Map<string, string>();
    for (const link of chip.interfaceLinks) {
      const label = link.label.trim();
      if (
        link.type !== 0 ||
        !link.structureTileId ||
        !label ||
        !Object.hasOwn(chip.adapterSignals ?? {}, label)
      )
        continue;
      const signalId = chip.adapterSignals![label];
      const previousLabel = identityLabels.get(signalId);
      if (previousLabel && previousLabel !== label)
        ctx.addIssue({
          code: 'custom',
          path: ['adapterSignals'],
          message: 'Different authored signal labels must have different stable IDs',
        });
      identityLabels.set(signalId, label);
    }
    const allowed = new Set<TileType>([
      TileType.SingleInterface,
      TileType.Structure,
      TileType.BundleInterface,
    ]);
    chip.structureCircuitDto.tileManagerDto.tiles.forEach((tile, index) => {
      if (!allowed.has(tile.tileType))
        ctx.addIssue({
          code: 'custom',
          path: ['structureCircuitDto', 'tileManagerDto', 'tiles', index],
          message:
            'Bundle adapters allow only Single Interface, Structure, and Bundle Interface tiles',
        });
    });
  });

export type ChipModelDto = z.infer<typeof ChipModelDtoSchema>;

// --- Project DTO ---

export const ProjectEnvelopeDtoSchema = z.object({
  /** Shared copies open paused until the recipient explicitly starts simulation. */
  startPaused: z.literal(true).optional(),
  name: z.string(),
  mainChipId: ChipIdSchema,
  chips: z.array(ChipModelDtoSchema),
});

/** Check references without expanding runtime graphs. Missing definitions are retained
 * in the caller's input and rejected before any active project is replaced. */
export function assertProjectDefinitionReferences(
  project: {
    mainChipId: string;
    chips: readonly {
      id: string;
      name: string;
      templateType?: 'logic' | 'structure';
      chipInstanceDtos: readonly { chipId: string }[];
    }[];
  },
  allowTemplateRoot = false,
): void {
  const definitions = new Map(project.chips.map((chip) => [chip.id, chip]));
  if (definitions.size !== project.chips.length) throw new Error('Duplicate chip IDs.');
  const root = definitions.get(project.mainChipId);
  if (!root) throw new Error('The main chip is missing.');
  if (root.templateType && !allowTemplateRoot)
    throw new Error('A template cannot be the project main chip.');
  for (const owner of project.chips)
    for (const instance of owner.chipInstanceDtos) {
      const dependency = definitions.get(instance.chipId);
      if (!dependency)
        throw new Error(`A chip dependency is missing from ${owner.name}.`);
      if (dependency.templateType)
        throw new Error(
          `Templates must be stamped as editable contents, not referenced as chip instances (${owner.name}).`,
        );
    }
}

export const ProjectDtoSchema = ProjectEnvelopeDtoSchema.superRefine((project, ctx) => {
  try {
    assertProjectDefinitionReferences(project);
  } catch (error) {
    ctx.addIssue({ code: 'custom', path: ['chips'], message: (error as Error).message });
  }
});

export type ProjectDto = z.infer<typeof ProjectDtoSchema>;

// --- Chip Export Bundle ---

export const ChipExportBundleSchema = z.object({
  version: z.literal(1),
  exportedAt: z.string(),
  rootChipId: ChipIdSchema,
  chips: z.array(ChipModelDtoSchema),
});

export type ChipExportBundle = z.infer<typeof ChipExportBundleSchema>;
